import type { RelatedDisplayItem, RelatedItemState } from '#layers/blocks/components/Relation/RelatedItemListCard.vue';

interface BuildAssignmentDisplayItemsOptions<TItem> {
	/** Persisted rows of the currently displayed server page. */
	pageItems: TItem[];
	/** One-based number of the displayed server page. */
	pageNumber: number;
	/** Staged additions that are not saved yet. */
	pendingItems: TItem[];
	/** Resolves the list-card row state for an assignment. */
	getItemState: (id: string) => RelatedItemState;
}

/**
 * Merge staged additions into a page of persisted assignments.
 *
 * Follows the relation-field convention of `useO2MState` / `useM2MState`: staged rows belong to no
 * server page, so they are appended to page one only and never shift the offsets of later pages.
 */
export function buildAssignmentDisplayItems<TItem extends { id: string }>(
	options: BuildAssignmentDisplayItemsOptions<TItem>,
): RelatedDisplayItem<TItem>[] {
	const { pageItems, pageNumber, pendingItems, getItemState } = options;

	const displayItems = pageItems.map((item) => toDisplayItem(item, getItemState));
	if (pageNumber !== 1) return displayItems;

	// A just-saved assignment briefly exists twice: as a fetched row and as a still-staged addition.
	// Drop the staged copy as soon as the server page returns it.
	const pageIds = new Set(pageItems.map(({ id }) => id));

	return [
		...displayItems,
		...pendingItems
			.filter(({ id }) => !pageIds.has(id))
			.map((item) => toDisplayItem(item, getItemState)),
	];
}

/** Wraps one assignment in the shape the related-item list card renders. */
function toDisplayItem<TItem extends { id: string }>(
	item: TItem,
	getItemState: (id: string) => RelatedItemState,
): RelatedDisplayItem<TItem> {
	// Assignments only ever reference existing roles/policies.
	return { id: item.id, data: item, state: getItemState(item.id), persisted: true };
}
